import { useMemo, useRef, useState } from "react";
import dayjs from "dayjs";
import { Loader2, Upload } from "lucide-react";
import { toast } from "sonner";

import { API_URL } from "@/constants";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useAdminBidDocuments } from "@/hooks/queries/useAdminBidDocuments";
import {
  useBidDocumentState,
  usePrepareBidDocuments,
  useUploadBidDocument,
} from "@/hooks/queries/useWorkAdvisoryBases";
import type {
  BidDocumentReviewReason,
  BidDocumentReviewReport,
} from "@/types/domain";
import {
  EMPTY_ERROR_FILTERS,
  type ErrorFiltersValue,
} from "@/components/admin/ContractingErrorFilters";
import {
  ContractingReportTable,
  type ReportColumn,
} from "@/components/admin/ContractingReportTable";

/**
 * Bases por revisar: las que no se pudieron leer. Todas se arreglan igual
 * —subiendo el PDF correcto—, que reemplaza la versión y la vuelve a leer en el
 * acto.
 */

// Alineado con MAX_INLINE_BYTES del backend: más grande no se puede leer.
const MAX_FILE_BYTES = 40 * 1024 * 1024;

const REASONS: Record<
  BidDocumentReviewReason,
  { label: string; hint: string; pill: string }
> = {
  TOO_HEAVY: {
    label: "Pesa más de 40 MB",
    hint: "Sube el mismo PDF con menor resolución.",
    pill: "bg-amber-50 text-amber-700 dark:bg-amber-950/30 dark:text-amber-300",
  },
  NOT_PDF: {
    label: "No es PDF",
    hint: "Conviértelo a PDF y súbelo.",
    pill: "bg-amber-50 text-amber-700 dark:bg-amber-950/30 dark:text-amber-300",
  },
  TOO_MANY_PAGES: {
    label: "Más de 1000 páginas",
    hint: "Sube solo las bases, sin anexos.",
    pill: "bg-amber-50 text-amber-700 dark:bg-amber-950/30 dark:text-amber-300",
  },
  NO_HARD_REQUIREMENTS: {
    label: "Sin requisitos obligatorios",
    hint: "Suele ser otro documento: sube las bases del SEACE.",
    pill: "bg-rose-50 text-rose-700 dark:bg-rose-950/30 dark:text-rose-300",
  },
  OTHER_FAILURE: {
    label: "Otro fallo",
    hint: "Revisa el archivo y sube uno correcto.",
    pill: "bg-rose-50 text-rose-700 dark:bg-rose-950/30 dark:text-rose-300",
  },
};

/** El archivo actual, con el motivo y qué subir en su lugar. */
function FileAndReason({ row }: { row: BidDocumentReviewReport }) {
  const reason = REASONS[row.reason];
  // El fallback a `/files/` cubre las versiones que nunca se subieron a R2.
  const href =
    row.fileUrl ??
    (row.legacyFilePath ? `${API_URL}/files/${row.legacyFilePath}` : null);

  return (
    <div className="flex flex-col gap-1">
      <div className="flex flex-wrap items-center gap-2">
        <span
          className={cn(
            "whitespace-nowrap rounded-full px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider",
            reason.pill,
          )}
        >
          {reason.label}
        </span>
        <span className="text-[11px] text-muted-foreground">{row.fileKind}</span>
      </div>
      {href ? (
        <a
          href={href}
          target="_blank"
          rel="noreferrer"
          title="Abrir el archivo actual"
          className="line-clamp-1 cursor-pointer text-xs text-primary hover:underline"
        >
          {row.fileName}
        </a>
      ) : (
        <span className="line-clamp-1 text-xs text-muted-foreground">
          {row.fileName}
        </span>
      )}
      <span className="line-clamp-2 text-xs text-muted-foreground">
        {row.lastError}. {reason.hint}
      </span>
    </div>
  );
}

/**
 * Cómo va la lectura después de subir. Solo se monta tras una subida: con cien
 * filas, preguntar el estado de todas al abrir la página serían cien consultas.
 */
function ReadingStatus({ contractingId }: { contractingId: number }) {
  const { data } = useBidDocumentState(contractingId);
  if (!data) return null;

  const tone =
    data.status === "LEIDAS"
      ? "text-emerald-700 dark:text-emerald-400"
      : data.status === "FALLO"
        ? "text-destructive"
        : "text-muted-foreground";

  return (
    <span className={cn("flex items-center gap-1.5 text-xs", tone)}>
      {(data.status === "EN_PROCESO" || data.status === "PENDIENTE") && (
        <Loader2 className="size-3 animate-spin" />
      )}
      {data.status === "LEIDAS"
        ? `Leídas · ${data.requirements} requisito(s)`
        : data.detail}
    </span>
  );
}

/** Sube el PDF corregido y lanza la lectura de una vez. */
function UploadFix({ row }: { row: BidDocumentReviewReport }) {
  const inputRef = useRef<HTMLInputElement>(null);
  const upload = useUploadBidDocument();
  const prepare = usePrepareBidDocuments();
  const [uploaded, setUploaded] = useState(false);

  async function handleFile(file: File) {
    if (file.size > MAX_FILE_BYTES) {
      toast.error(`${file.name} pesa más de 40 MB.`);
      return;
    }
    try {
      await upload.mutateAsync({ contractingId: row.contractingId, file });
      await prepare.mutateAsync({ contractingId: row.contractingId });
      setUploaded(true);
    } catch (error) {
      toast.error(
        error instanceof Error ? error.message : "No se pudo subir el archivo.",
      );
    }
  }

  const busy = upload.isPending || prepare.isPending;

  return (
    <div className="flex flex-col items-start gap-1.5">
      <Button
        size="sm"
        variant="outline"
        disabled={busy}
        onClick={() => inputRef.current?.click()}
      >
        {busy ? (
          <Loader2 className="size-3.5 animate-spin" />
        ) : (
          <Upload className="size-3.5" />
        )}
        {busy ? "Subiendo…" : uploaded ? "Subir otro PDF" : "Subir PDF corregido"}
      </Button>
      {uploaded && <ReadingStatus contractingId={row.contractingId} />}
      <input
        ref={inputRef}
        type="file"
        accept=".pdf"
        hidden
        onChange={(event) => {
          const file = event.target.files?.[0];
          if (file) handleFile(file);
          event.target.value = "";
        }}
      />
    </div>
  );
}

const COLUMNS: ReportColumn<BidDocumentReviewReport>[] = [
  {
    key: "file",
    header: "Archivo y motivo",
    className: "min-w-[320px] max-w-[460px]",
    render: (row) => <FileAndReason row={row} />,
  },
  {
    key: "fix",
    header: "Solución",
    className: "w-px whitespace-nowrap",
    render: (row) => <UploadFix row={row} />,
  },
];

export default function AdminBidDocuments() {
  const [filters, setFilters] =
    useState<ErrorFiltersValue>(EMPTY_ERROR_FILTERS);
  const [reason, setReason] = useState<"all" | BidDocumentReviewReason>("all");
  const { data = [], isLoading } = useAdminBidDocuments({
    stages: filters.stages,
    publicatedFrom: filters.publicatedFrom || undefined,
    // Fin del día para que el rango incluya la fecha elegida.
    publicatedTo: filters.publicatedTo
      ? dayjs(filters.publicatedTo).endOf("day").toISOString()
      : undefined,
    sortBy: filters.sortBy,
  });

  const rows = useMemo(
    () => (reason === "all" ? data : data.filter((row) => row.reason === reason)),
    [data, reason],
  );

  return (
    <ContractingReportTable
      title="Bases por revisar"
      description="Bases que no se pudieron leer. Se arreglan subiendo el PDF correcto."
      filters={filters}
      onFiltersChange={setFilters}
      extraFilters={
        <div className="flex flex-col gap-1.5">
          <Label className="text-xs text-muted-foreground">Motivo</Label>
          <Select
            value={reason}
            onValueChange={(value) => setReason(value as typeof reason)}
          >
            <SelectTrigger className="sm:w-[230px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos los motivos</SelectItem>
              {(Object.keys(REASONS) as BidDocumentReviewReason[]).map((key) => (
                <SelectItem key={key} value={key}>
                  {REASONS[key].label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      }
      extraFiltersActive={reason !== "all"}
      rows={rows}
      columns={COLUMNS}
      isLoading={isLoading}
      emptyMessage="No hay bases por revisar."
    />
  );
}
